/**
 * Recipe Format Types
 *
 * Defines the canonical recipe.json file format — a self-contained bundle that
 * includes a workflow definition, mock data, trigger sample data, and
 * connections in a single file.
 *
 * This is the *file-format* type. Runtime state shapes live in
 * `templateMode.ts`. An adapter layer in `data/recipes/recipeAdapter.ts`
 * converts Recipe → TemplateDefinition + RecipeExtras.
 *
 * KEEP IN SYNC: the public recipes repo (Azure/ProjectAutoRecipes) carries a
 * manual copy of this file (and `workflowDefinition.ts`) in `schemas/` for CI
 * validation. When changing this schema, update the copies there in the same
 * release, or its CI will validate a different format than the portal reads.
 */

import { z } from 'zod';
import { WorkflowDefinitionSchema } from '@/types/workflowDefinition';

// =============================================================================
// SCHEMAS
// =============================================================================

const recipeMetadataSchema = z.object({
  /** Stable, URL-safe identifier (e.g. "tool-call-uri-http"). */
  id: z.string().min(1),
  /** Human-readable recipe name. */
  name: z.string().min(1),
  /** One-to-two sentence description. */
  description: z.string().min(1),
  /**
   * One or more free-form category strings (mapped to `TemplateCategory[]`
   * by the adapter). Accepts a bare string for back-compat with recipes
   * authored before multi-category support — normalized to a non-empty
   * array at parse time.
   */
  category: z
    .union([z.string().min(1), z.array(z.string().min(1)).min(1)])
    .transform((v) => (Array.isArray(v) ? v : [v])),
  /** Search / filter tags. */
  tags: z.array(z.string().min(1)).default([]),
  /** Author or organisation. */
  author: z.string().min(1),
  /** Provenance label (e.g. "builtin", "community"). */
  source: z.string().min(1),
});

const recipeTriggerSchema = z.object({
  /** Trigger action name (must match a key in definition.triggers). */
  name: z.string().min(1),
  /** Sample outputs used to pre-fill the Test Draft editor. */
  outputs: z.object({
    method: z.string().min(1),
    headers: z.record(z.string(), z.unknown()),
    queries: z.record(z.string(), z.unknown()),
    body: z.unknown().optional(),
  }),
});

// Per-action mock entry. Two valid shapes:
//   • `{ runForReal: true }` — explicit opt-out; the runtime executes the
//     action live and no canned output is needed.
//   • `{ status, outputs }` (with `runForReal` absent or false) — the runtime
//     uses the canned output instead of executing the action.
const recipeMockOutputSchema = z.union([
  z.object({
    runForReal: z.literal(true),
  }),
  z.object({
    runForReal: z.literal(false).optional(),
    /** Mock run status (e.g. "Succeeded", "Failed", "TimedOut"). */
    status: z.string().min(1),
    outputs: z.object({
      statusCode: z.string().min(1),
      body: z.unknown().optional(),
      headers: z.record(z.string(), z.unknown()).optional(),
    }),
  }),
]);

const recipeConnectionSchema = z.object({
  /** Connection type: 'shared' for managed APIs, 'inapp' for built-in connectors, 'agent' for agent connections */
  connectorType: z.enum(['shared', 'inapp', 'agent']),
  /** API identifier path, e.g. '/managedApis/sql' or '/serviceProviders/AzureBlob' */
  apiId: z.string().min(1),
});

export const recipeSchema = z.object({
  $schema: z.string().optional(),
  kind: z.literal('OttoRecipe'),
  apiVersion: z.literal('v1'),

  metadata: recipeMetadataSchema,
  definition: WorkflowDefinitionSchema,
  trigger: recipeTriggerSchema,
  mocks: z.record(z.string(), recipeMockOutputSchema).default({}),
  // Sticky-note metadata copied from `notes.json` / `notes-draft.json` so
  // recipes round-trip the canvas annotations alongside mocks. Shape mirrors
  // `WorkflowDefinitionSchema.notes` (per-note id → { content, color, metadata }).
  notes: WorkflowDefinitionSchema.shape.notes,
  connections: z.record(
    z.string().regex(/^\S*_#workflowname#$/, {
      message: 'connections key must end with _#workflowname#',
    }),
    recipeConnectionSchema,
  ).default({}),
  featuredConnectors: z.array(
    z.object({
      id: z.string().min(1),
      displayName: z.string().min(1),
      /** Full URL or local path (e.g. /connectorIcons/request.svg) */
      iconUrl: z.string().min(1),
    })
  ).optional(),
});

// =============================================================================
// TYPES
// =============================================================================

export type Recipe = z.infer<typeof recipeSchema>;
export type RecipeMetadata = z.infer<typeof recipeMetadataSchema>;
export type RecipeTrigger = z.infer<typeof recipeTriggerSchema>;
export type RecipeMockOutput = z.infer<typeof recipeMockOutputSchema>;
export type RecipeConnection = z.infer<typeof recipeConnectionSchema>;

// =============================================================================
// VALIDATION HELPER
// =============================================================================

/**
 * Validate an unknown value against `recipeSchema`.
 *
 * Returns a discriminated result:
 * - `{ success: true,  data: Recipe }` on success
 * - `{ success: false, errors: string[] }` on failure
 */
export function validateRecipe(
  data: unknown,
): { success: true; data: Recipe } | { success: false; errors: string[] } {
  const result = recipeSchema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  const errors = result.error.issues.map((issue) => {
    const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
    return `${path}${issue.message}`;
  });

  return { success: false, errors };
}
